// API client for Wattelzce microservice

const BASE_URL = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

export interface Device {
  id: number;
  deviceId: string | null;
  name: string;
  type: string;
  model: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  firmwareVersion: string | null;
  ocppVersion: string | null;
  registrationStatus: string | null;
  lastSeen: string | null;
  createdAt: string | null;
}

export interface DeviceDetail extends Device {
  iccid: string | null;
  imsi: string | null;
  meterType: string | null;
  meterSerialNumber: string | null;
  updatedAt: string | null;
}

export interface DeviceStats {
  totalDevices: number;
  activeDevices: number;
  inactiveDevices: number;
  devicesByType: Array<{ type: string; count: number }>;
  devicesByStatus: Array<{ status: string; count: number }>;
  recentActivity: {
    last24h: number;
    last7d: number;
  };
}

// New interfaces for analytics
export interface KpiData {
  totalEnergyKwh: number;
  totalCost: number;
  totalPlugs: number;
  totalUsageHours: number;
  // Additional KPIs from devices table
  totalDevices?: number;
  activeDevices?: number;
  inactiveDevices?: number;
  recentlyActive?: number;
  totalTransactions?: number;
}

export interface EnergyByMonth {
  month: string;
  year: number;
  totalEnergy: number;
  transactionCount?: number;
}

export interface TopDeviceEnergy {
  deviceId: number;
  deviceName: string;
  totalEnergy: number;
  transactionCount?: number;
}

export interface RevenueByDevice {
  deviceId: number;
  deviceName: string;
  totalRevenue: number;
  transactionCount?: number;
}

export interface EnergyByVehicle {
  vehicleName: string;
  totalEnergy: number;
  transactionCount?: number;
}

export interface DeviceNameOption {
  deviceId: number;
  deviceName: string;
}

export interface DeviceVendorOption {
  vendor: string;
}

export interface FilterParams {
  startDate?: string;
  endDate?: string;
  deviceName?: string;
  deviceVendor?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  fromCache?: boolean;
}

// Helper to get auth token
function getAuthToken(): string | null {
  return localStorage.getItem('dashboard_zce_token');
}

// Helper to create auth headers
function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
}

async function fetchApi<T>(endpoint: string): Promise<ApiResponse<T>> {
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

function buildQueryString(params: FilterParams): string {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.set('startDate', params.startDate);
  if (params.endDate) searchParams.set('endDate', params.endDate);
  if (params.deviceName) searchParams.set('deviceName', params.deviceName);
  if (params.deviceVendor) searchParams.set('deviceVendor', params.deviceVendor);
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

export const wattelzceApi = {
  // Existing endpoints
  getDevices: (params?: { page?: number; limit?: number; status?: string; type?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.type) searchParams.set('type', params.type);
    
    const query = searchParams.toString();
    return fetchApi<Device[]>(`/api/devices${query ? `?${query}` : ''}`);
  },

  getDevice: (id: number) => {
    return fetchApi<Device>(`/api/devices/${id}`);
  },

  getStats: () => {
    return fetchApi<DeviceStats>('/api/devices/stats/summary');
  },

  getHealth: () => {
    return fetchApi<{ status: string; timestamp: string }>('/health');
  },

  // New analytics endpoints
  getKpis: (params?: FilterParams) => {
    return fetchApi<KpiData>(`/api/devices/analytics/kpis${buildQueryString(params || {})}`);
  },

  getEnergyByMonth: (params?: FilterParams) => {
    return fetchApi<EnergyByMonth[]>(`/api/devices/analytics/energy-by-month${buildQueryString(params || {})}`);
  },

  getTopDevicesEnergy: (params?: FilterParams) => {
    return fetchApi<TopDeviceEnergy[]>(`/api/devices/analytics/top-devices-energy${buildQueryString(params || {})}`);
  },

  getRevenueByDevice: (params?: FilterParams) => {
    return fetchApi<RevenueByDevice[]>(`/api/devices/analytics/revenue-by-device${buildQueryString(params || {})}`);
  },

  getEnergyByVehicle: (params?: FilterParams) => {
    return fetchApi<EnergyByVehicle[]>(`/api/devices/analytics/energy-by-vehicle${buildQueryString(params || {})}`);
  },

  // Filter options
  getDeviceNames: () => {
    return fetchApi<DeviceNameOption[]>('/api/devices/list/device-names');
  },

  getDeviceVendors: () => {
    return fetchApi<DeviceVendorOption[]>('/api/devices/list/device-vendors');
  },
};
